/**
 * Libellés français des rôles.
 *
 * L'API renvoie les rôles d'un utilisateur sous forme de noms techniques
 * (`roles: user.roles.map(ur => ur.role.name)`), et toute la logique de
 * l'application s'appuie sur ces noms — redirections, contrôles d'accès,
 * filtres. On ne les remplace donc pas : on les traduit uniquement à
 * l'affichage.
 *
 * Les libellés reprennent la colonne display_name de la table roles.
 */
const LIBELLES: Record<string, string> = {
  admin: 'Administrateur',
  dg: 'Directeur Général',
  director_general: 'Directeur Général',
  accountant: 'Comptable',
  commercial: 'Commercial',
  farm_manager: 'Gestionnaire Ferme',
  cold_room_manager: 'Responsable Chambre Froide',
  cold_storage_manager: 'Responsable Chambre Froide',
  resp_multi_cf: 'Responsable Multi-CF',
  shop_manager: 'Responsable Boutique',
  resp_multi_boutique: 'Responsable Multi-Boutiques',
  shop_seller: 'Vendeur Boutique',
  production_manager: 'Responsable Production',
  driver: 'Livreur',
  delivery_driver: 'Livreur',
  customer: 'Client',
  support: 'Support Client',
};

/**
 * Rend un rôle lisible. Accepte aussi bien une chaîne technique qu'un objet
 * renvoyé par l'API, les deux formes circulant dans l'application.
 * Un rôle inconnu n'est jamais masqué : il est simplement mis en forme.
 */
export const libelleRole = (role: unknown): string => {
  if (!role) return 'Rôle';

  if (typeof role === 'object') {
    const r = role as { display_name?: string; displayName?: string; name?: string; role?: string };
    const libelle = r.display_name || r.displayName;
    if (libelle) return libelle;
    return libelleRole(r.name || r.role);
  }

  const nom = String(role).trim();
  if (LIBELLES[nom]) return LIBELLES[nom];

  // Rôle absent de la table : on évite d'afficher du technique brut.
  return nom
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Familles de rôles.
 *
 * Les rôles multi-sites ont été oubliés quatre fois de suite : dans la table
 * des droits, dans les fenêtres de création de boutique et de chambre froide,
 * dans la résolution du contexte de travail, et dans la redirection après
 * connexion. Chaque fois le symptôme était « ça ne marche pas », sans message.
 *
 * Ces deux fonctions sont la seule référence : un responsable multi-sites est
 * un responsable, avec un périmètre plus large. Toute nouvelle vérification de
 * rôle passe par ici.
 */
const normaliser = (nom: unknown): string =>
  String(nom ?? '').toLowerCase().replace(/[-_\s]/g, '');

/**
 * Responsable d'une boutique ou de toutes les boutiques.
 *
 * "commercial" est strictement equivalent a "shop_manager" — meme fonction,
 * deux comptes possibles (Acteur 1 du document de reference) — jamais un
 * role a droits reduits. Il suit donc exactement le meme chemin : meme
 * redirection a la connexion, meme ecran, memes actions.
 */
export const estResponsableBoutique = (role: unknown): boolean => {
  const n = normaliser(typeof role === 'object' && role !== null
    ? (role as { name?: string }).name : role);
  return n === 'shopmanager' || n === 'storemanager'
    || n === 'responsableboutique' || n === 'respmultiboutique'
    || n === 'commercial';
};

/** Responsable d'une chambre froide ou de toutes les chambres froides. */
export const estResponsableCF = (role: unknown): boolean => {
  const n = normaliser(typeof role === 'object' && role !== null
    ? (role as { name?: string }).name : role);
  return n === 'coldstoragemanager' || n === 'coldroommanager'
    || n === 'responsablechambrefroide' || n === 'respmulticf';
};

/**
 * Les rôles regroupés en mini-catégories.
 *
 * A plat, treize intitulés techniques ne se distinguent qu'en les lisant un à
 * un — dans la liste des utilisateurs comme dans le formulaire de création.
 * Une seule référence pour les deux écrans, pour qu'ils ne divergent jamais.
 */
export const CATEGORIES_ROLES: Array<{ id: string; label: string; roles: string[] }> = [
  { id: 'direction', label: 'Direction', roles: ['admin', 'dg'] },
  { id: 'boutiques', label: 'Boutiques', roles: ['shop_manager', 'resp_multi_boutique', 'shop_seller'] },
  { id: 'chambres_froides', label: 'Chambres froides', roles: ['cold_storage_manager', 'cold_room_manager', 'resp_multi_cf'] },
  { id: 'ferme', label: 'Ferme', roles: ['farm_manager', 'farm_agent'] },
  { id: 'finance_achats', label: 'Finance & Achats', roles: ['accountant', 'commercial'] },
  { id: 'logistique', label: 'Logistique', roles: ['delivery_driver', 'driver'] },
  { id: 'support', label: 'Support', roles: ['support'] },
  { id: 'clients', label: 'Clients', roles: ['customer'] },
];

/**
 * La catégorie d'un rôle, ou "autres" si aucune ne le cite.
 *
 * Prend le rôle tel qu'il arrive — une chaîne ("admin") ou un objet
 * ({name, display_name}), comme libelleRole. Les appelants passaient
 * `role.name` en dur : l'API renvoie des chaînes simples, donc `.name` y
 * valait toujours undefined et tout le monde tombait dans "Autres" — les
 * onglets Direction/Boutiques/... affichaient (0) quel que soit le vrai
 * rôle affiché juste à côté sur la même ligne.
 */
export const categorieDuRole = (role: unknown): string => {
  const n = typeof role === 'object' && role !== null
    ? String((role as { name?: string; role?: string }).name || (role as { role?: string }).role || '')
    : String(role || '');
  return CATEGORIES_ROLES.find((c) => c.roles.includes(n))?.id || 'autres';
};
